import { Dispatch, SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../api/users-api';
import { DriveSweepModel } from '../../../../types';

import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { pagesConfig } from '../../../shared/pages-config';
import { useNavigate } from 'react-router-dom';

import SharedGridStateIconButton from '../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../shared/analytics/module-list';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import CreateButton from '../../../shared/buttons/create-button';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';

interface DriveSweepToolbarProps {
  driveRoles: string[];
  selectedDriveSweeps: DriveSweepModel[];
  setOpenDisableSweeps: Dispatch<SetStateAction<boolean>>;
  setOpenEnableSweeps: Dispatch<SetStateAction<boolean>>;
  setOpenDeleteDriveSweep: Dispatch<SetStateAction<boolean>>;
}

export default function DriveSweepToolbar({
  driveRoles,
  selectedDriveSweeps,
  setOpenDisableSweeps,
  setOpenEnableSweeps,
  setOpenDeleteDriveSweep,
}: DriveSweepToolbarProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const containsDisabledSweeps = selectedDriveSweeps.some(
    (sweep) => sweep.isActive === false
  );
  const containsEnabledSweeps = selectedDriveSweeps.some(
    (sweep) => sweep.isActive === true
  );

  return (
    <SharedToolbar
      primaryButtons={
        <CreateButton
          onClick={() => {
            navigate(
              `/${pagesConfig.driveSweep.link}/${pagesConfig.createDriveSweepFormPage.link}`
            );
          }}
          disabled={
            !isAbleToSeeUIElement(
              driveRoles,
              DriveRoles.CanCreateDriveSweep.value
            )
          }
          label="Create Drive Sweep"
          tooltipLabel={
            !isAbleToSeeUIElement(
              driveRoles,
              DriveRoles.CanCreateDriveSweep.value
            )
              ? `You are missing the "${DriveRoles.CanCreateDriveSweep.name}" permission to perform this action.`
              : ''
          }
        />
      }
      secondaryButtons={
        <>
          <SharedIconButton
            variant="enable"
            onClick={() => setOpenEnableSweeps(true)}
            disabled={
              selectedDriveSweeps.length === 0 ||
              !isAbleToSeeUIElement(
                driveRoles || [],
                DriveRoles.UpdateDriveSweepStatus.value
              ) ||
              containsEnabledSweeps
            }
            tooltipLabel={
              containsEnabledSweeps
                ? 'All selected sweeps must be disabled'
                : !isAbleToSeeUIElement(
                    driveRoles,
                    DriveRoles.UpdateDriveSweepStatus.value
                  )
                ? `You are missing the "${DriveRoles.UpdateDriveSweepStatus.name}" permission to perform this action.`
                : 'Enable'
            }
          />
          <SharedIconButton
            variant="block"
            onClick={() => setOpenDisableSweeps(true)}
            disabled={
              selectedDriveSweeps.length === 0 ||
              !isAbleToSeeUIElement(
                driveRoles || [],
                DriveRoles.UpdateDriveSweepStatus.value
              ) ||
              containsDisabledSweeps
            }
            tooltipLabel={
              containsDisabledSweeps
                ? 'All selected sweeps must be enabled'
                : !isAbleToSeeUIElement(
                    driveRoles,
                    DriveRoles.UpdateDriveSweepStatus.value
                  )
                ? `You are missing the "${DriveRoles.UpdateDriveSweepStatus.name}" permission to perform this action.`
                : 'Disable'
            }
          />

          <SharedIconButton
            variant="delete"
            onClick={() => {
              setOpenDeleteDriveSweep(true);
            }}
            disabled={
              selectedDriveSweeps.length === 0 ||
              !isAbleToSeeUIElement(
                driveRoles || [],
                DriveRoles.CanDeleteDriveSweep.value
              )
            }
            tooltipLabel={
              !isAbleToSeeUIElement(
                driveRoles,
                DriveRoles.CanDeleteDriveSweep.value
              )
                ? `You are missing the "${DriveRoles.CanDeleteDriveSweep.name}" permission to perform this action.`
                : 'Delete'
            }
          />
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveSweepModels],
              });

              queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveSweepLogs],
              });
            }}
            tooltipLabel="Refresh"
          />
          <SharedGridStateIconButton
            gridStateKey="driveSweepsGridStates"
            module={ModuleList.modules.drive.name}
            section={ModuleList.modules.drive.sections.driveSweep}
          />
        </>
      }
    />
  );
}
